import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import styled from 'styled-components';
import { Check } from 'lucide-react';
import { Cbx, Input, Muted } from '../ui/primitives';
import { AUDIENCE_MODE } from './publishingListApi';

/**
 * Chooses which organizations see one course in the instructor import catalog.
 *
 * Mode is explicit rather than inferred from the selection, because "selected
 * organizations, none picked" (nobody sees it) and "all organizations" are different
 * intentions that would otherwise collapse into the same empty state.
 */
export default function CourseAudienceDialog({
  open,
  course,
  organizations,
  orgsLoading,
  orgsError,
  saving,
  onClose,
  onSave,
}) {
  const [mode, setMode] = useState(AUDIENCE_MODE.allOrgs);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [query, setQuery] = useState('');

  // Re-seed from the row every time the dialog opens so a cancelled edit never
  // leaks into the next course the admin clicks.
  useEffect(() => {
    if (!open || !course) return;
    setMode(Number(course.catalogAudienceMode) === AUDIENCE_MODE.selectedOrgs
      ? AUDIENCE_MODE.selectedOrgs
      : AUDIENCE_MODE.allOrgs);
    setSelectedOrgIds([...(course.audienceOrgIds || [])]);
    setQuery('');
  }, [open, course]);

  const selectedSet = useMemo(() => new Set(selectedOrgIds), [selectedOrgIds]);

  const visibleOrgs = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return organizations || [];
    return (organizations || []).filter((org) => (
      String(org.orgName || org.orgId || '').toLowerCase().includes(needle)
    ));
  }, [organizations, query]);

  const toggleOrg = (orgId) => {
    setSelectedOrgIds((current) => (
      current.includes(orgId)
        ? current.filter((id) => id !== orgId)
        : [...current, orgId]
    ));
  };

  const restricted = mode === AUDIENCE_MODE.selectedOrgs;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Catalog audience
        <Muted style={{ display: 'block', fontSize: 13, fontWeight: 500, marginTop: 2 }}>
          {course?.courseName || 'Course'}
        </Muted>
      </DialogTitle>

      <DialogContent dividers>
        <ModeChoice>
          <label>
            <input
              type="radio"
              name="catalog-audience-mode"
              checked={!restricted}
              onChange={() => setMode(AUDIENCE_MODE.allOrgs)}
            />
            <div>
              <strong>All organizations</strong>
              <Muted>Every instructor who can browse the catalog sees this course.</Muted>
            </div>
          </label>
          <label>
            <input
              type="radio"
              name="catalog-audience-mode"
              checked={restricted}
              onChange={() => setMode(AUDIENCE_MODE.selectedOrgs)}
            />
            <div>
              <strong>Selected organizations only</strong>
              <Muted>Only instructors in the organizations you pick below.</Muted>
            </div>
          </label>
        </ModeChoice>

        {restricted && (
          <>
            {orgsLoading && <LoadingWrap><CircularProgress size={22} /></LoadingWrap>}
            {!orgsLoading && orgsError && <Alert severity="error">{orgsError}</Alert>}

            {!orgsLoading && !orgsError && (
              <>
                <Input
                  type="search"
                  placeholder="Search organizations…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ width: '100%', margin: '14px 0 10px' }}
                />

                {selectedOrgIds.length === 0 && (
                  <Alert severity="warning" style={{ marginBottom: 10 }}>
                    No organizations selected — no instructor will see this course in the catalog.
                  </Alert>
                )}

                <OrgList>
                  {visibleOrgs.map((org) => (
                    <OrgRow key={org.orgId} onClick={() => toggleOrg(org.orgId)}>
                      <Cbx $on={selectedSet.has(org.orgId)}><Check size={13} /></Cbx>
                      <div>
                        <strong>{org.orgName || '(unnamed)'}</strong>
                        <Muted>{org.orgId}</Muted>
                      </div>
                    </OrgRow>
                  ))}
                  {visibleOrgs.length === 0 && (
                    <Muted style={{ padding: '12px 2px' }}>No organizations match that search.</Muted>
                  )}
                </OrgList>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || (restricted && orgsLoading)}
          onClick={() => onSave({ mode, orgIds: selectedOrgIds })}
        >
          {saving ? 'Saving…' : 'Save audience'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ModeChoice = styled.div`
  display: grid;
  gap: 10px;

  label {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    cursor: pointer;
  }

  input { margin-top: 3px; }
  strong { display: block; font-size: 13.5px; font-weight: 640; }
  span { display: block; font-size: 12.5px; }
`;

const LoadingWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 28px 0;
`;

const OrgList = styled.div`
  max-height: 280px;
  overflow-y: auto;
  display: grid;
  gap: 2px;
`;

const OrgRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 6px;
  border-radius: 8px;
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.surface2}; }
  strong { display: block; font-size: 13px; font-weight: 600; }
  span { display: block; font-size: 11.5px; }
`;
